// src/pages/Services.js
import React from "react";
import {
  FaLaptopCode,
  FaShoppingCart,
  FaGlobe,
  FaArrowRight,
  FaBriefcase,
  FaCalendarAlt,
  FaChartLine
} from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import SEO from '../components/SEO';
import PageHero from '../components/page/PageHero';
import SectionHeading from '../components/page/SectionHeading';
import ProofStrip from '../components/page/ProofStrip';
import ProcessSteps from '../components/page/ProcessSteps';
import WorkCarousel from '../components/page/WorkCarousel';
import servicesHero from '../assets/services/services-hero.png';
import arixaShot from '../assets/work/arixa.webp';
import jitsShot from '../assets/work/jesus-is-the-solution.webp';
import axialisShot from '../assets/work/axialis.webp';
import basemintShot from '../assets/work/basemint-studios.webp';

const Services = () => {
  const { t } = useTranslation();

  const primaryTextColor = "text-silver-light";
  const secondaryTextColor = "text-silver-medium";

  // Called unconditionally, in a fixed order — a wrapper helper here would trip
  // the rules-of-hooks lint even though the order is stable.
  const revealOpts = { triggerOnce: true, threshold: 0.2, rootMargin: "-50px" };
  const [servicesRef, servicesInView] = useInView(revealOpts);
  const [pricingRef, pricingInView] = useInView(revealOpts);
  const [workRef, workInView] = useInView(revealOpts);
  const [processRef, processInView] = useInView(revealOpts);

  // Services data - mirrors the home page cards
  const services = [
    { id: 'customWebApp', icon: <FaLaptopCode className="text-4xl mb-5" />, link: '/custom-development' },
    { id: 'ecommerce', icon: <FaShoppingCart className="text-4xl mb-5" />, link: '/limelite' },
    { id: 'businessWebsite', icon: <FaGlobe className="text-4xl mb-5" />, link: '/business-websites' },
    { id: 'portfolio', icon: <FaBriefcase className="text-4xl mb-5" />, link: '/business-websites' },
    { id: 'booking', icon: <FaCalendarAlt className="text-4xl mb-5" />, link: '/booking-websites' },
    { id: 'consulting', icon: <FaChartLine className="text-4xl mb-5" />, link: '/consulting' },
  ];

  // Real, permissioned client work only — live sites with real screenshots.
  // Arixa leads as the flagship build. Other past projects are deliberately
  // omitted until the client has agreed to be shown.
  const work = [
    {
      name: 'Arixa',
      image: arixaShot,
      tag: t('services.work.tags.webApp', 'Web application'),
      blurb: t('services.work.arixa', 'A full web application — user accounts, playlists and a custom media pipeline, built and shipped end to end.'),
      href: 'https://app.arixa.io',
      featured: true,
    },
    {
      name: 'Axialis',
      image: axialisShot,
      tag: t('services.work.tags.brandSite', 'Brand site'),
      blurb: t('services.work.axialis', 'A site for an EU-funded engineering company developing axial flux electric motors — interactive product reveal, technical detail made readable, and the funding disclosure their grant requires.'),
      href: 'https://axialis.hr',
    },
    {
      name: 'BaseMint Studios',
      image: basemintShot,
      tag: t('services.work.tags.brandSite', 'Brand site'),
      blurb: t('services.work.basemint', 'Designed, built and deployed for an indie game studio — a launch site for their first title, pointing wishlists at Steam and fans at the studio.'),
      href: 'https://basemintstudios.com',
    },
    {
      name: 'Jesus Is The Solution',
      image: jitsShot,
      tag: t('services.work.tags.contentSite', 'Content site'),
      blurb: t('services.work.jits', 'A content-led website built for clarity and fast loading.'),
      href: 'https://jesus-is-the-solution.com',
    },
  ];

  const processSteps = [
    {
      title: t('services.process.step1.title', 'You tell us about the project'),
      text: t('services.process.step1.text', 'Four fields. No account to create, nothing to install.'),
    },
    {
      title: t('services.process.step2.title', 'We talk it through'),
      text: t('services.process.step2.text', 'A short call to understand what you need and whether we are the right fit.'),
    },
    {
      title: t('services.process.step3.title', 'You get a written scope and price'),
      text: t('services.process.step3.text', 'Agreed in writing before any work begins, so the cost never moves under you.'),
    },
  ];

  const servicesSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Custom Web Development Services",
        "serviceType": "Custom website and web application development",
        "provider": {
          "@type": "Organization",
          "name": "Riviera Tech Solutions",
          "url": "https://rivieratech.solutions"
        },
        "areaServed": [{ "@type": "Country", "name": "Croatia" }],
        "url": "https://rivieratech.solutions/services",
        "description": "Custom web development services for custom websites, web applications, and ecommerce website development."
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What custom web development services do you provide?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We build custom websites, custom web applications, ecommerce website development projects, booking systems, and business process software."
            }
          },
          {
            "@type": "Question",
            "name": "How much do custom web development projects cost?",
            "acceptedAnswer": {
              // Deliberately no figures: the previous €199/€499 numbers here were
              // out of date and were being served to search engines as current.
              // Quote per project rather than publish a price that can go stale.
              "text": "Every project is quoted individually after a free 30-minute consultation, because cost depends entirely on scope. You get a written scope and price before any work begins."
            }
          },
          {
            "@type": "Question",
            "name": "How do you keep projects secure?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We apply secure defaults across the stack, including validation, access control, and defensive handling of malicious traffic patterns."
            }
          }
        ]
      }
    ]
  };

  return (
    <div className="bg-black font-sans">
      <SEO
        title="Web Development Services for Croatian Businesses"
        description="Custom websites, online stores and booking systems for businesses in Zadar and across Croatia. Free 30-minute consultation, written scope and fixed price before work begins."
        keywords="web development Croatia, custom website Zadar, booking system Croatia, ecommerce website Croatia"
        url="/services"
        schema={servicesSchema}
      />

      <PageHero
        eyebrow={t('services.hero.tagline', 'Riviera Tech Solutions Services')}
        title={t('services.hero.title', 'Websites and software built for Croatian businesses')}
        subtitle={t('services.hero.subtitle', 'Custom websites, online stores and booking systems — designed, built and looked after by the engineer who writes the code.')}
        ctaLabel={t('services.hero.consultationButton', 'Book a free 30-minute consultation')}
        ctaTo="/contact"
        secondaryLabel={t('services.hero.workButton', 'See our work')}
        secondaryTo="/business-websites"
        proofLine={t('services.hero.proof', 'Rated 5.0 on Google · Free consultation · Fixed price agreed before work starts')}
        backgroundImage={servicesHero}
      />

      {/* Proof sits immediately under the hero: 74% of viewing time is spent in
          the first two screenfuls, so proof placed lower is largely unseen. */}
      <ProofStrip />

      {/* What we build */}
      <section ref={servicesRef} className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <SectionHeading
            eyebrow={t('services.grid.eyebrow', 'What we build')}
            title={t('home.services.title', 'Our Services')}
            lead={t('services.buildingWebsites.description', 'We build and deploy custom web solutions that set you apart online.')}
            className="mb-12"
          />

          <div
            className={`mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3 transition-all duration-1000 transform ${servicesInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            {services.map((service) => (
              <Link
                key={service.id}
                to={service.link}
                className="group rounded-lg border border-gray-800 bg-off-black p-8 transition-colors duration-300 hover:border-moss/60"
              >
                <div className="text-moss-light">{service.icon}</div>
                <h3 className={`mb-3 text-xl font-medium ${primaryTextColor}`}>
                  {t(`home.services.cards.${service.id}.title`)}
                </h3>
                <p className={`mb-5 text-sm leading-relaxed ${secondaryTextColor}`}>
                  {t(`home.services.cards.${service.id}.description`)}
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-medium text-moss-light">
                  {t('services.grid.more', 'Read more')}
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* No price list. Cost appearing late is a real driver of drop-off, so
          instead of a number that can go stale we commit to WHEN the price
          appears and that it is fixed in writing before work starts. */}
      <section ref={pricingRef} className="py-20 bg-moss-night border-y border-moss-deep/40">
        <div className="container mx-auto px-4">
          <div
            className={`mx-auto max-w-3xl text-center transition-all duration-1000 transform ${pricingInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <SectionHeading
              eyebrow={t('services.pricing.eyebrow', 'What it costs')}
              title={t('services.pricing.title', 'Quoted per project, agreed in writing')}
              lead={t('services.pricing.lead', 'Every project is different, so we price it after we understand it — not before. The first 30-minute consultation is free, and you get a written scope and a fixed price before any work begins.')}
            />
            <Link
              to="/contact"
              className="mt-8 inline-block rounded-md border border-moss/60 px-6 py-3 text-base font-medium text-silver-light transition-colors duration-300 hover:border-moss hover:bg-moss hover:text-black"
            >
              {t('services.pricing.cta', 'Get a free quote')}
            </Link>
          </div>
        </div>
      </section>

      {/* Real, shipped work */}
      <section ref={workRef} className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <SectionHeading
            eyebrow={t('services.work.eyebrow', 'Recent work')}
            title={t('services.work.title', 'Projects we have shipped')}
            className="mb-12"
          />
          {/* A slider rather than a grid: screenshots are the argument here, so
              cards need to stay large as the portfolio grows. */}
          <div
            className={`transition-all duration-1000 transform ${workInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <WorkCarousel
              items={work}
              featuredLabel={t('services.work.featured', 'Featured build')}
              visitLabel={t('services.work.visit', 'Visit the site')}
            />
          </div>
        </div>
      </section>

      {/* What happens next */}
      <section ref={processRef} className="py-20 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4">
          <SectionHeading
            eyebrow={t('services.process.eyebrow', 'What happens next')}
            title={t('services.process.title', 'From first message to agreed price')}
            className="mb-12"
          />
          <div
            className={`mx-auto max-w-5xl transition-all duration-1000 transform ${processInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <ProcessSteps steps={processSteps} />

            <div className="mt-14 text-center">
              <Link
                to="/contact"
                className="inline-block rounded-md bg-moss px-8 py-3 text-lg font-medium text-black transition-colors duration-300 hover:bg-moss-light"
              >
                {t('services.process.cta', 'Book a 30-minute consultation')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
